<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Manually-entered team grid.
 *
 * This block was promoted out of agency-saas, which has production tenants
 * using it. The card markup below is reproduced verbatim from that version, and
 * everything this block adds — the headline, bios and social links — is guarded
 * by a `v-if` on a key that no existing payload carries. Existing data
 * therefore renders exactly as it did before the promotion.
 *
 * Live-data variants stay in the consuming app: an app registers its own
 * wrapper under the same `team` key, switches on `data.source`, and delegates
 * back to this component for the manual case.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('')

const members = computed(() => props.data.members ?? [])

const isExternal = (url: string) => /^(https?:|mailto:|tel:)/.test(url)

// Single-path icons, keyed by platform. Inline because public blocks in this
// layer deliberately depend on no icon component.
const SOCIAL_PATHS: Record<string, string> = {
  facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
  instagram: 'M12 2.2c3.2 0 3.6 0 4.9.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.9c-.1 3.2-1.6 4.8-4.9 4.9-1.3.1-1.6.1-4.9.1s-3.6 0-4.9-.1c-3.3-.1-4.8-1.7-4.9-4.9-.1-1.3-.1-1.6-.1-4.9s0-3.5.1-4.8C2.3 4 3.8 2.4 7.1 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 3.6a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4zm0 10.2a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-10.4a1.4 1.4 0 1 1-2.9 0 1.4 1.4 0 0 1 2.9 0z',
  linkedin: 'M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.1c.5-1 1.8-2 3.7-2 4 0 4.7 2.6 4.7 6V21h-4v-5.5c0-1.3 0-3-1.9-3s-2.1 1.4-2.1 2.9V21H9z',
  x: 'M18.9 2H22l-7 8 8.2 12h-6.4l-5-7.3L6 22H2.9l7.5-8.6L2.5 2H9l4.5 6.6zm-1.1 18h1.7L7.3 3.8H5.5z',
  youtube: 'M23 12s0-3.6-.5-5.3a2.8 2.8 0 0 0-2-2C18.8 4.2 12 4.2 12 4.2s-6.8 0-8.5.5a2.8 2.8 0 0 0-2 2C1 8.4 1 12 1 12s0 3.6.5 5.3a2.8 2.8 0 0 0 2 2c1.7.5 8.5.5 8.5.5s6.8 0 8.5-.5a2.8 2.8 0 0 0 2-2c.5-1.7.5-5.3.5-5.3zM9.8 15.3V8.7l5.7 3.3z',
  website: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 0c2.5 2.7 3.9 6.3 4 10-.1 3.7-1.5 7.3-4 10-2.5-2.7-3.9-6.3-4-10 .1-3.7 1.5-7.3 4-10zM2.5 9h19M2.5 15h19',
  email: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm0 2 8 6 8-6',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z'
}
</script>


<template>
  <!-- Deliberately multi-root: a payload that predates this block's
       promotion (no headline) must emit exactly the same DOM the app-local
       version did, so agency's live pages do not shift. Vue renders SiteSectionHeading's
       own v-if=false as a comment node when there is no headline, same as it
       already does for every other conditional branch in this template. -->
  <SiteSectionHeading
    :eyebrow="data.eyebrow"
    :title="data.headline"
    class="mb-6"
  />
  <div
    v-if="members.length"
    class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
  >
    <div
      v-for="(m, i) in members"
      :key="i"
      class="site-surface-card flex items-center gap-3 p-4"
    >
      <img
        v-if="m.avatar"
        :src="m.avatar"
        :alt="m.name"
        class="size-12 rounded-full object-cover"
      >
      <span
        v-else
        class="site-bg-accent-soft site-text-accent flex size-12 shrink-0 items-center justify-center rounded-full text-sm font-bold"
      >{{ initials(m.name) }}</span>
      <div class="min-w-0">
        <p class="truncate font-semibold">
          {{ m.name }}
        </p>
        <p
          v-if="m.role"
          class="truncate text-sm text-[var(--site-muted)]"
        >
          {{ m.role }}
        </p>
        <p
          v-if="m.bio"
          class="mt-1 text-sm leading-relaxed text-[var(--site-muted)]"
        >
          {{ m.bio }}
        </p>
        <div
          v-if="m.socials?.length"
          class="mt-2 flex items-center gap-2"
        >
          <a
            v-for="(social, si) in m.socials"
            :key="si"
            :href="social.url"
            :target="isExternal(social.url) && !social.url.startsWith('mailto:') && !social.url.startsWith('tel:') ? '_blank' : undefined"
            rel="noopener noreferrer"
            class="text-[var(--site-muted)] transition-colors hover:text-[var(--site-accent)]"
            :aria-label="`${m.name} on ${social.platform}`"
          >
            <svg
              class="size-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              aria-hidden="true"
            >
              <path
                :d="SOCIAL_PATHS[social.platform] ?? SOCIAL_PATHS.website"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
